import { createApp } from 'vue';

import { initAnalytics } from '@/platform/analytics';
import { vGa } from '@/platform/analytics/v-ga';
import {
  drainPrivateLinkLoginCookies,
  handOffToPrivateLink,
} from '@/platform/auth-runtime/private-link-handoff';
import { getSubBus } from '@/platform/host-sdk/host-bridge';
import { installBroadcaster } from '@/platform/host-sdk/sub-broadcaster';
import { useUserStore } from '@/platform/identity/store';
import { pinia } from '@/platform/pinia';
import { router } from '@/platform/routing';
import { captureVueError, init as initFrontendAlert } from '@/platform/telemetry/frontend-alert';
import { syncServerTime } from '@/shared/lib/clock/time-sync';
import { queryClient } from '@/shared/lib/query-client';
import { startWs } from '@/shared/lib/ws/ws';
import { installFormErrorA11y } from '@/shared/ui/form-error-a11y';
import { VueQueryPlugin } from '@tanstack/vue-query';
import dayjs from 'dayjs';
import '@/platform/auth-runtime/cross-tab-logout';
import '@/domains/notifications/ws-bridge';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import WujieVue from 'wujie-vue3';

import App from './App.vue';
import { i18n } from './lang';

import 'element-plus/theme-chalk/dark/css-vars.css';
import './styles/tailwind.css';
import './styles/element.css';

dayjs.extend(utc);
dayjs.extend(timezone);

async function bootstrap() {
  if (import.meta.env.VITE_MOCK === 'true') {
    const { worker, warnOnUnmockedApi } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: warnOnUnmockedApi });
  }

  // Initialize telemetry before the first axios call so rejected requests
  // can be reported by the shared HTTP layer.
  initFrontendAlert();

  // SSO callback on the public domain writes session tokens into a cookie
  // before bouncing to the private-link subdomain; drain into localStorage
  // before fetchInfo so the host doesn't redirect a successful SSO sign-in
  // to /login.
  drainPrivateLinkLoginCookies();

  // Mirror case: pack the session back and bounce to the private-link host;
  // don't mount an app the redirect throws away.
  if (handOffToPrivateLink()) return;

  // Warm the legacy sub bundle now so its HTML/CSS/JS download overlaps the
  // serial identity bootstrap below (syncServerTime + fetchInfo) instead of
  // queueing after it. preloadApp only fetches — the sub's scripts don't run
  // and getInitialState (LegacyShellHost) isn't read until startApp on first
  // shell mount, which is still after fetchInfo, so the identity gate holds.
  //
  // exec must NOT be true here. wujie 1.0.29's preloadApp with exec:true
  // runs sub scripts BEFORE startApp passes :props to sandbox.active(),
  // so $wujie.props is undefined during sub module-eval. wujie-impl.js
  // would then capture empty caps + null seed, leaving mirror.user/path
  // null when main.js init() reads them → handleRoute can't proceed and
  // legacy resolves to /404 via the catch-all. preloadApp without exec
  // still warms the HTML/CSS fetch; scripts run on first startApp when
  // props are wired.
  WujieVue.preloadApp({ name: 'legacy', url: import.meta.env.VITE_LEGACY_SUB_URL });

  // Sync the client clock to the server before any request-ticket headers
  // are signed.
  await syncServerTime();

  const app = createApp(App);
  // Vue render / lifecycle errors don't reach window.onerror; route them
  // into the telemetry pipeline with their real stack + component info.
  app.config.errorHandler = (err, instance, info) => {
    captureVueError(err, instance, info);
  };

  app.use(pinia);
  if (await useUserStore().fetchInfo()) void startWs();

  app.use(router);
  installBroadcaster(getSubBus(), router);
  app.use(i18n);
  app.use(VueQueryPlugin, { queryClient });
  app.directive('ga', vGa);
  // GA dual-tag: product tag plus optional marketing-site tag. v3 pageview
  // firing is driven by router.afterEach inside initAnalytics.
  initAnalytics(router);

  // Wait for the initial navigation — including its lazy route component —
  // to resolve before mounting, so the inline init mask stays up until the
  // first view can paint instead of flashing white while the chunk loads.
  await router.isReady();
  installFormErrorA11y();
  app.mount('#app');
}

void bootstrap();
